import { c as _c } from "react/compiler-runtime";
import { getOrgAgentsMcpServersQueryKey, postOrgAgentsMcpServersMutation, postOrgAgentsMcpServersOauthStartMutation } from '@onefootprint/fetch-dashboard';
import { useRequestErrorToast } from '@onefootprint/hooks';
import type { AgentMcpServer, AgentMcpServerAuthKind } from '@onefootprint/request-types/dashboard';
import { Dialog, Divider, Form, InlineAlert, SegmentedControl, Toggle } from '@onefootprint/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useMcpOauthPopup, { mcpOauthRedirectUri } from '../../use-mcp-oauth-popup';
type AddMcpServerDialogProps = {
  open: boolean;
  onClose: () => void;
  onCreated: (server: AgentMcpServer) => void;
  initialAuthKind?: AgentMcpServerAuthKind;
};
type FormData = {
  name: string;
  url: string;
  authKind: AgentMcpServerAuthKind;
  headerName: string;
  headerValue: string;
  clientId: string;
  clientSecret: string;
};
const emptyForm = {
  name: '',
  url: '',
  headerName: '',
  headerValue: '',
  clientId: '',
  clientSecret: ''
};
const isHttpsUrl = (value: string) => {
  try {
    return new URL(value).protocol === 'https:';
  } catch {
    return false;
  }
};
const AddMcpServerDialog = t0 => {
  const $ = _c(63);
  const {
    open,
    onClose,
    onCreated,
    initialAuthKind: t1
  } = t0;
  const initialAuthKind = t1 === undefined ? "oauth" : t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      keyPrefix: "agentBuilder.tools.mcp.dialog"
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const {
    t
  } = useTranslation("agents", t2);
  const queryClient = useQueryClient();
  const showErrorToast = useRequestErrorToast();
  const [showClientFields, setShowClientFields] = useState(false);
  const [blockedUrl, setBlockedUrl] = useState(null);
  let t3;
  if ($[1] !== initialAuthKind) {
    t3 = {
      defaultValues: {
        ...emptyForm,
        authKind: initialAuthKind
      }
    };
    $[1] = initialAuthKind;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const form = useForm(t3);
  const {
    handleSubmit,
    reset,
    control
  } = form;
  let t4;
  if ($[3] !== control) {
    t4 = {
      control,
      name: "authKind"
    };
    $[3] = control;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const authKind = useWatch(t4);
  let t5;
  if ($[5] !== onCreated || $[6] !== queryClient || $[7] !== reset) {
    t5 = async server => {
      await queryClient.invalidateQueries({
        queryKey: getOrgAgentsMcpServersQueryKey()
      });
      reset();
      setShowClientFields(false);
      setBlockedUrl(null);
      onCreated(server);
    };
    $[5] = onCreated;
    $[6] = queryClient;
    $[7] = reset;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const finish = t5;
  let t6;
  if ($[9] !== finish) {
    t6 = {
      onComplete: finish
    };
    $[9] = finish;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const popup = useMcpOauthPopup(t6);
  let t7;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = postOrgAgentsMcpServersMutation();
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== finish || $[13] !== showErrorToast) {
    t8 = {
      ...t7,
      onSuccess: finish,
      onError: showErrorToast
    };
    $[12] = finish;
    $[13] = showErrorToast;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  const headerMutation = useMutation(t8);
  let t9;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = postOrgAgentsMcpServersOauthStartMutation();
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== popup) {
    t10 = t11 => {
      const {
        authorizationUrl
      } = t11;
      if (!popup.open(authorizationUrl)) {
        setBlockedUrl(authorizationUrl);
      }
    };
    $[16] = popup;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== showErrorToast || $[19] !== t10) {
    t11 = {
      ...t9,
      onSuccess: t10,
      onError: showErrorToast
    };
    $[18] = showErrorToast;
    $[19] = t10;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  const oauthMutation = useMutation(t11);
  const isPending = headerMutation.isPending || oauthMutation.isPending;
  let t12;
  if ($[21] !== isPending || $[22] !== onClose || $[23] !== popup || $[24] !== reset) {
    t12 = () => {
      if (isPending) {
        return;
      }
      popup.cancel();
      reset();
      setShowClientFields(false);
      setBlockedUrl(null);
      onClose();
    };
    $[21] = isPending;
    $[22] = onClose;
    $[23] = popup;
    $[24] = reset;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  const handleClose = t12;
  let t13;
  if ($[26] !== headerMutation || $[27] !== oauthMutation || $[28] !== showClientFields) {
    t13 = data => {
      if (data.authKind === "header") {
        headerMutation.mutate({
          body: {
            name: data.name,
            url: data.url,
            headerName: data.headerName,
            headerValue: data.headerValue
          }
        });
        return;
      }
      oauthMutation.mutate({
        body: {
          name: data.name,
          url: data.url,
          redirectUri: mcpOauthRedirectUri(),
          clientId: showClientFields ? data.clientId || undefined : undefined,
          clientSecret: showClientFields ? data.clientSecret || undefined : undefined
        }
      });
    };
    $[26] = headerMutation;
    $[27] = oauthMutation;
    $[28] = showClientFields;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  const onSubmit = t13;
  const isWaiting = popup.isWaiting || blockedUrl !== null;
  let t14;
  if ($[30] !== authKind || $[31] !== t) {
    t14 = authKind === "oauth" ? t(_temp) : t(_temp2);
    $[30] = authKind;
    $[31] = t;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  const submitLabel = t14;
  let t15;
  if ($[33] !== t) {
    t15 = t(_temp3);
    $[33] = t;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] !== isPending || $[36] !== isWaiting || $[37] !== submitLabel) {
    t16 = isWaiting ? undefined : {
      form: "add-mcp-server-form",
      label: submitLabel,
      loading: isPending,
      type: "submit"
    };
    $[35] = isPending;
    $[36] = isWaiting;
    $[37] = submitLabel;
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  let t17;
  if ($[39] !== t) {
    t17 = t(_temp4);
    $[39] = t;
    $[40] = t17;
  } else {
    t17 = $[40];
  }
  let t18;
  if ($[41] !== handleClose || $[42] !== isPending || $[43] !== t17) {
    t18 = {
      label: t17,
      disabled: isPending,
      onClick: handleClose
    };
    $[41] = handleClose;
    $[42] = isPending;
    $[43] = t17;
    $[44] = t18;
  } else {
    t18 = $[44];
  }
  let t19;
  if ($[45] !== authKind || $[46] !== blockedUrl || $[47] !== control || $[48] !== form || $[49] !== handleSubmit || $[50] !== isWaiting || $[51] !== onSubmit || $[52] !== popup || $[53] !== showClientFields || $[54] !== t) {
    t19 = isWaiting ? <div className="flex flex-col gap-3" role="status"><p className="text-body-2 text-secondary">{t(_temp5)}</p>{blockedUrl && <InlineAlert variant="warning" cta={{
        label: t(_temp6),
        onClick: () => {
          if (popup.open(blockedUrl)) {
            setBlockedUrl(null);
          }
        }
      }}>{t(_temp7)}</InlineAlert>}</div> : <FormProvider {...form}><form id="add-mcp-server-form" onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5"><Form.Input size="compact" name="name" label={t(_temp8)} placeholder={t(_temp9)} autoComplete="off" rules={{
          required: t(_temp10)
        }} /><Form.Input size="compact" name="url" label={t(_temp11)} placeholder="https://mcp.example.com/mcp" autoComplete="off" rules={{
          required: t(_temp12),
          validate: value => isHttpsUrl(value) || t(_temp13)
        }} /><Divider /><div className="flex flex-col gap-4"><div className="flex flex-wrap items-center justify-between gap-2"><span className="text-label-2 text-primary">{t(_temp14)}</span><Controller control={control} name="authKind" render={t20 => {
              const {
                field
              } = t20;
              return <SegmentedControl aria-label={t(_temp15)} size="compact" value={field.value} onChange={field.onChange} options={[{
                value: "oauth",
                label: t(_temp16)
              }, {
                value: "header",
                label: t(_temp17)
              }]} />;
            }} /></div>{authKind === "header" && <div className="flex flex-col gap-5"><Form.Input size="compact" name="headerName" label={t(_temp18)} placeholder={t(_temp19)} autoComplete="off" rules={{
              required: t(_temp20)
            }} /><Form.Input size="compact" name="headerValue" type="password" label={t(_temp21)} hint={t(_temp22)} placeholder={"Bearer \u2026"} autoComplete="new-password" data-dd-privacy="mask" rules={{
              required: t(_temp23)
            }} /></div>}{authKind === "oauth" && <><p className="text-body-2 text-primary">{t(_temp24)}</p><div className="flex flex-col gap-5"><Toggle size="compact" label={t(_temp25)} checked={showClientFields} onChange={setShowClientFields} />{showClientFields && <><Form.Input size="compact" name="clientId" label={t(_temp26)} placeholder={t(_temp27)} hint={t(_temp28, {
                  redirectUri: mcpOauthRedirectUri()
                })} autoComplete="off" /><Form.Input size="compact" name="clientSecret" type="password" label={t(_temp29)} placeholder={t(_temp30)} hint={t(_temp31)} autoComplete="new-password" data-dd-privacy="mask" /></>}</div></>}</div></form></FormProvider>;
    $[45] = authKind;
    $[46] = blockedUrl;
    $[47] = control;
    $[48] = form;
    $[49] = handleSubmit;
    $[50] = isWaiting;
    $[51] = onSubmit;
    $[52] = popup;
    $[53] = showClientFields;
    $[54] = t;
    $[55] = t19;
  } else {
    t19 = $[55];
  }
  let t20;
  if ($[56] !== handleClose || $[57] !== open || $[58] !== t15 || $[59] !== t16 || $[60] !== t18 || $[61] !== t19) {
    t20 = <Dialog open={open} onClose={handleClose} title={t15} size="compact" primaryButton={t16} secondaryButton={t18}>{t19}</Dialog>;
    $[56] = handleClose;
    $[57] = open;
    $[58] = t15;
    $[59] = t16;
    $[60] = t18;
    $[61] = t19;
    $[62] = t20;
  } else {
    t20 = $[62];
  }
  return t20;
};
export default AddMcpServerDialog;
function _temp($) {
  return $.connect;
}
function _temp2($_0) {
  return $_0.submit;
}
function _temp3($_1) {
  return $_1.title;
}
function _temp4($_2) {
  return $_2.cancel;
}
function _temp5($_3) {
  return $_3.waiting.description;
}
function _temp6($_4) {
  return $_4.waiting.openPage;
}
function _temp7($_5) {
  return $_5.waiting.popupBlocked;
}
function _temp8($_6) {
  return $_6.name.label;
}
function _temp9($_7) {
  return $_7.name.placeholder;
}
function _temp10($_8) {
  return $_8.name.required;
}
function _temp11($_9) {
  return $_9.url.label;
}
function _temp12($_10) {
  return $_10.url.required;
}
function _temp13($_11) {
  return $_11.url.invalid;
}
function _temp14($_12) {
  return $_12.authKind.label;
}
function _temp15($_13) {
  return $_13.authKind.label;
}
function _temp16($_14) {
  return $_14.authKind.oauth;
}
function _temp17($_15) {
  return $_15.authKind.header;
}
function _temp18($_16) {
  return $_16.headerName.label;
}
function _temp19($_17) {
  return $_17.headerName.placeholder;
}
function _temp20($_18) {
  return $_18.headerName.required;
}
function _temp21($_19) {
  return $_19.headerValue.label;
}
function _temp22($_20) {
  return $_20.headerValue.hint;
}
function _temp23($_21) {
  return $_21.headerValue.required;
}
function _temp24($_22) {
  return $_22.oauthHint;
}
function _temp25($_23) {
  return $_23.showClientFields;
}
function _temp26($_24) {
  return $_24.clientId.label;
}
function _temp27($_25) {
  return $_25.clientId.placeholder;
}
function _temp28($_26) {
  return $_26.clientId.hint;
}
function _temp29($_27) {
  return $_27.clientSecret.label;
}
function _temp30($_28) {
  return $_28.clientSecret.placeholder;
}
function _temp31($_29) {
  return $_29.clientSecret.hint;
}